import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Link2, X } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { useResponsesStore } from '@/stores/responses-store'
import { getResponseTargetPath } from '@/lib/response-linking'
import { ResponsePickerPopover } from '@/components/extraction/ResponsePickerPopover'
import { cn } from '@/lib/utils'

interface ResponseLinkCellProps {
  bidId: string
  linkedArtifactId: string | null
  linkedSectionKey: string | null
  linkedArtifactName?: string | null
  linkedSectionTitle?: string | null
  readOnly?: boolean
  /**
   * Width rule for the linked label. Defaults to the Analysis table's 120px
   * cap; the Pack row has a wider column and lets the label fill it.
   */
  fillWidth?: boolean
  onLink: (responseId: string, sectionKey: string | null) => void | Promise<void>
  onUnlink: () => void | Promise<void>
  onCreate: () => void | Promise<void>
}

export function ResponseLinkCell({
  bidId,
  linkedArtifactId,
  linkedSectionKey,
  linkedArtifactName,
  linkedSectionTitle,
  readOnly,
  fillWidth = false,
  onLink,
  onUnlink,
  onCreate,
}: ResponseLinkCellProps) {
  const navigate = useNavigate()
  const artifacts = useResponsesStore((state) => state.artifacts)
  const ensureArtifacts = useResponsesStore((state) => state.ensureArtifacts)

  useEffect(() => {
    void ensureArtifacts(bidId)
  }, [bidId, ensureArtifacts])

  const linkedArtifact = useMemo(
    () => artifacts.find((artifact) => artifact.id === linkedArtifactId) ?? null,
    [artifacts, linkedArtifactId]
  )

  if (linkedArtifactId) {
    const label = linkedSectionTitle ?? linkedArtifactName ?? linkedArtifact?.name ?? 'Linked response'

    return (
      <div className={cn('group flex items-center gap-1', fillWidth && 'min-w-0')}>
        <Link2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <button
          type="button"
          title={label}
          className={cn(
            'truncate text-xs text-[hsl(var(--link))] hover:underline',
            fillWidth ? 'min-w-0 flex-1 text-left' : 'max-w-[120px]'
          )}
          onClick={() => {
            if (!linkedArtifact) return
            void navigate(getResponseTargetPath(linkedArtifact, bidId, linkedSectionKey))
          }}
        >
          {label}
        </button>
        {!readOnly && <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              type="button"
              className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity rounded-sm hover:bg-muted p-0.5"
              title="Remove link"
            >
              <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="max-w-xs">
            <AlertDialogTitle className="text-sm">Remove link?</AlertDialogTitle>
            <AlertDialogDescription className="text-xs">
              This will unlink the response from this submission item. You can re-link it later.
            </AlertDialogDescription>
            <AlertDialogFooter>
              <AlertDialogCancel className="h-8 text-xs">Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="h-8 text-xs"
                onClick={() => void onUnlink()}
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>}
      </div>
    )
  }

  if (readOnly) {
    return <span className="text-meta">—</span>
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="group/link flex items-center gap-1.5 text-meta hover:text-foreground transition-colors"
        >
          <Link2 className="h-3 w-3 opacity-0 group-hover/link:opacity-100 transition-opacity" />
          <span className="group-hover/link:underline">Link response</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <ResponsePickerPopover
          bidId={bidId}
          onSelect={onLink}
          onCreate={onCreate}
        />
      </PopoverContent>
    </Popover>
  )
}
